import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { useCoinMarket } from '@/services/coin/market'
import { formatPercentageColor, formatPercentageWithLimit } from '@/utils/number/format'

/**
 * Custom Hook: Fetch and format coin price change information
 *
 * @param coinName - The coin name (will be automatically converted to lowercase)
 * @returns Object containing coin market data and formatted price change information
 *
 * @example
 * ```tsx
 * const { coinMarket, percentage, changeColor, changeSymbol } = useCoinPriceChange('Bitcoin');
 *
 * if (!coinMarket) return <div>Loading...</div>;
 *
 * return (
 *   <div>
 *     <span style={{ color: changeColor }}>
 *       {changeSymbol}{percentage}
 *     </span>
 *   </div>
 * );
 * ```
 */
export default function useCoinPriceChange(coinSlug: string) {
  // Fetch coin market data
  const { data: coinMarket } = useCoinMarket(coinSlug.toLowerCase(), true)

  const {
    amounts: [{ formattedValue: price }],
  } = useCurrencyAmounts({
    amounts: [coinMarket?.usd_price ?? 0],
    currencyFormatOptions: {},
    tokenSymbol: coinMarket?.app_symbol ?? '',
  })

  // Return default values if data is not loaded yet
  if (!coinMarket) {
    return {
      /** Coin market data, undefined when not loaded */
      coinMarket: undefined,
      /** Formatted price change percentage, empty string when not loaded */
      percentage: '',
      /** Color based on price movement, secondary text color when not loaded */
      changeColor: 'var(--text-secondary)',
      /** Price change symbol (+ or -), empty string when not loaded */
      changeSymbol: '',
      /** Price */
      price: '',
    }
  }

  // Calculate and format price change percentage
  const percentage = formatPercentageWithLimit((coinMarket?.usd_price_change_24h || 0) * 100)

  // Get color based on price movement (positive/negative)
  const changeColor = formatPercentageColor(Number(percentage.replace('%', '')))

  // Determine price change symbol: show + for positive, no symbol for negative/zero
  const changeSymbol = Number(coinMarket?.usd_price_change_24h) > 0 ? '+' : ''

  return {
    /** Complete coin market data */
    coinMarket,
    /** Formatted price change percentage string (includes % symbol) */
    percentage,
    /** CSS color value based on price movement */
    changeColor,
    /** Price change symbol (+ only for positive values) */
    changeSymbol,
    /** Price */
    price,
  }
}
